import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import styles from "./progressBar.module.css";

export const CONTENT_TRACK_CLASS = "progressBar__content";

const ProgressBar = () => {
  const [isMounted, setIsMounted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isRead, setIsRead] = useState(false);
  const articleText = useRef<Element | null>(null);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    if (typeof window === "undefined") return;

    if (isRead) {
      window.document.removeEventListener("scroll", raf);
      cancelAnimationFrame(rafRef.current);
      return;
    }

    const contentLists =
      window.document.getElementsByClassName(CONTENT_TRACK_CLASS);

    articleText.current =
      contentLists && contentLists.length > 0 ? contentLists[0] : null;

    if (!articleText.current) return;

    setIsMounted(true);

    window.document.addEventListener("scroll", raf);

    return () => {
      window.document.removeEventListener("scroll", raf);
    };

    function raf() {
      rafRef.current = requestAnimationFrame(calculateScrollDistance);
      return rafRef.current;
    }
  }, [isRead]);

  function calculateScrollDistance() {
    if (typeof window !== "undefined" && articleText.current) {
      const windowHeight = window.innerHeight;

      const { top, bottom, height } =
        articleText.current.getBoundingClientRect();

      const startYPoint = top - windowHeight;
      const endYPoint = bottom - windowHeight;

      if (startYPoint < 0) {
        const scrollPosition = Math.floor((1 - endYPoint / height) * 100);

        if (scrollPosition >= 100) {
          setIsRead(true);
        }
        setProgress(scrollPosition);
      } else {
        setProgress(0);
      }
    }
  }

  if (!articleText.current) return null;

  return (
    <div
      data-testid={"progressBar"}
      className={classNames(
        "pointer-events-none relative h-0 w-100",
        isRead && styles.complete
      )}
    >
      <div
        className={classNames(
          "absolute left-0 top-0 h-[4px] w-100 scale-y-0 overflow-hidden bg-[#DDDDE0]",
          isMounted && styles.mounted,
          isRead && styles.animate
        )}
      >
        <div
          data-testid={"progressBar__track"}
          style={{ transform: `scaleX(${progress}%)` }}
          className={classNames(
            "h-100 w-100 origin-left transition-transform ease-linear",
            progress > 0 && "bg-black-rock",
            isRead && "opacity-0"
          )}
        ></div>
      </div>
    </div>
  );
};

export default ProgressBar;
